import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getCollectibles, getCurrentRates } from '@/lib/api';
import Navbar from '@/components/Navbar';
import MetalTicker from '@/components/MetalTicker';
import Footer from '@/components/Footer';
import { Badge } from '@/components/ui/badge';
import { TrendingUp } from 'lucide-react';
import { formatSilverPurity } from '@/lib/productFormatting';
import { formatInr, MONEY_CLASSNAME } from '@/lib/money';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06 } }
};

function ProductCard({ item, rate, ratesAvailable }) {
  const totalPrice = item.display_price;
  const priceMessage = item.price_status === 'confirmation_required' ? 'Price confirmation required' : 'Live rate temporarily unavailable';

  return (
    <Link to={`/collectible/${item.collectible_id}`} className="group" data-testid={`product-card-${item.collectible_id}`}>
      <motion.div variants={fadeUp} className="bg-card border border-border/30 rounded-2xl overflow-hidden hover:border-silver/40 transition-all duration-300">
        <div className="aspect-square bg-gradient-to-br from-silver/5 to-secondary overflow-hidden relative">
          <img
            src={item.image_url || item.images?.[0] || 'https://images.unsplash.com/photo-1707569590646-2a51751bb9ef?w=600'}
            alt={item.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {item.quantity_remaining < 5 && item.quantity_remaining > 0 && (
            <Badge className="absolute top-3 right-3 bg-red-500/90 text-white text-[10px]">Only {item.quantity_remaining} left</Badge>
          )}
          {item.quantity_remaining === 0 && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <Badge className="bg-background/90 text-foreground">Sold Out</Badge>
            </div>
          )}
        </div>
        <div className="p-4">
          <h3 className="font-display text-base font-bold mb-1 group-hover:text-silver transition-colors line-clamp-1">
            {item.name}
          </h3>
          <p className="text-xs text-muted-foreground mb-2" data-testid={`product-purity-${item.collectible_id}`}>
            {formatSilverPurity(item)} • {item.weight}g
          </p>
          <div className="flex items-baseline justify-between mb-1">
            <p className={`${MONEY_CLASSNAME} text-xl font-semibold text-silver`}>
              {formatInr(totalPrice) || priceMessage}
            </p>
          </div>
          <div className="flex items-center">
            {ratesAvailable && <span className="text-[10px] text-green-400 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Live Silver Linked
            </span>}
          </div>
        </div>
      </motion.div>
    </Link>
  );
}

export default function SilverPage() {
  const [collectibles, setCollectibles] = useState([]);
  const [rates, setRates] = useState(null);
  const [filter, setFilter] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getCollectibles().then(r => r.data || []),
      getCurrentRates().then(r => r.data).catch(() => null)
    ]).then(([items, ratesData]) => {
      setCollectibles(items.filter(i => i.metal_type === 'silver'));
      setRates(ratesData);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Get unique categories that have items - only show categories with products
  const getAvailableCategories = () => {
    const catCounts = {};
    collectibles.forEach(item => {
      const cat = (item.category || item.design_type || 'other').toLowerCase();
      catCounts[cat] = (catCounts[cat] || 0) + 1;
    });
    return Object.keys(catCounts).map(cat => ({
      name: cat.charAt(0).toUpperCase() + cat.slice(1).replace('-', ' '),
      key: cat,
      count: catCounts[cat]
    }));
  };

  const availableCategories = getAvailableCategories();
  const allCategories = [{ name: 'All', key: 'All', count: collectibles.length }, ...availableCategories];

  const filtered = filter === 'All'
    ? collectibles
    : collectibles.filter(c => {
        const cat = (c.category || c.design_type || '').toLowerCase();
        return cat === filter.toLowerCase();
      });

  const silverRate = rates?.silver?.rate_per_gram || 0;
  const ratesAvailable = silverRate > 0 && (rates?.gold?.rate_per_gram || 0) > 0;

  return (
    <div className="min-h-screen bg-background" data-testid="silver-page">
      <Navbar />
      <MetalTicker rates={rates} />

      {/* Hero Section - Molten Silver */}
      <section className="relative h-64 md:h-80 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1681554913232-a501060263b0?w=1800&q=80"
          alt="Silver texture"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-5 md:px-8 w-full">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[11px] uppercase tracking-[0.3em] text-silver/70 mb-3">Silver Collection</p>
              <h1 className="font-display text-4xl md:text-6xl font-bold">
                Silver <span className="text-silver">Pieces</span>
              </h1>
              {ratesAvailable ? (
                <p className="text-sm text-muted-foreground mt-3">
                  Live Rate: <span className={MONEY_CLASSNAME}>{formatInr(silverRate)}/g</span>
                </p>
              ) : <p className="text-sm text-amber-400 mt-3" data-testid="silver-rate-unavailable">Live rate temporarily unavailable</p>}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Category Filter - Only show categories with items */}
      <section className="py-4 border-b border-border/20 sticky top-0 bg-background/95 backdrop-blur-md z-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {allCategories.map(cat => (
              <button
                key={cat.key}
                onClick={() => setFilter(cat.key)}
                className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center gap-2 ${
                  filter === cat.key
                    ? 'bg-silver text-background'
                    : 'bg-secondary/50 text-foreground/60 hover:bg-secondary hover:text-foreground'
                }`}
                data-testid={`category-${cat.key.toLowerCase()}`}
              >
                {cat.name}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  filter === cat.key ? 'bg-background/20' : 'bg-foreground/10'
                }`}>
                  {cat.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-card border border-border/30 rounded-2xl overflow-hidden animate-pulse">
                  <div className="aspect-square bg-secondary/50" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-secondary/50 rounded w-3/4" />
                    <div className="h-3 bg-secondary/50 rounded w-1/2" />
                    <div className="h-6 bg-secondary/50 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length > 0 ? (
            <motion.div
              initial="hidden" animate="visible" variants={stagger}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
            >
              {filtered.map(item => (
                <ProductCard key={item.collectible_id} item={item} rate={silverRate} ratesAvailable={ratesAvailable} />
              ))}
            </motion.div>
          ) : (
            <div className="text-center py-20">
              <p className="text-muted-foreground">No silver pieces found in this category.</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
